import {Permission} from "./auth/Permission";
import User from "./User";
import {AiExtractorSource} from "./AiExtractorSource";
import {ResourceOwnerType} from "./ResourceOwnerType";
import Organization from "./Organization";
import {AiProviderConnectionType} from "./AiProviderConnection";
import ItemUpdateProfile from "./ItemUpdateProfile";
import {UpdatedItemsBehaviour} from "./UpdatedItemsBehaviour";

export interface AiExtractorVulSource {
    id?: string;
    name?: string;
    owner?: ResourceOwnerType;
    organizationName?: string;
    scope?: ResourceOwnerType;
}

export interface AiExtractorAiAttribute {
    id?: string;
    name?: string;
}

export interface AiExtractorPrompt {
    id?: string;
    name?: string;
}

export interface AiExtractorProviderConnection {
    id?: string;
    name?: string;
    type?: AiProviderConnectionType;
}

export interface SupportedModel {
    name: string;
    supportedOpenAiServiceTiers: OpenAiServiceTier[];
    supportedOpenAiReasoningEfforts: OpenAiReasoningEffort[];
    defaultOpenAiReasoningEffort?: OpenAiReasoningEffort;
    supportedAnthropicReasoningEfforts: AnthropicReasoningEffort[];
    defaultAnthropicReasoningEffort?: AnthropicReasoningEffort;
    supportedGrokReasoningEfforts: GrokReasoningEffort[];
    defaultGrokReasoningEffort?: GrokReasoningEffort;
    supportsWebSearch: boolean;
}

type InterestManagedAiExtractor = {
    managedByInterests?: boolean;
    aiAttributes?: AiExtractorAiAttribute[];
};

export enum AiExtractorType {
    OPENAI_API = "OPENAI_API",
    OPENAI_COMPATIBLE_API = "OPENAI_COMPATIBLE_API",
    GEMINI_API = "GEMINI_API",
    GROK_API = "GROK_API",
    OLLAMA = "OLLAMA",
    MISTRAL_AI = "MISTRAL_AI",
    ANTHROPIC_API = "ANTHROPIC_API"
}

export enum OpenAiServiceTier {
    DEFAULT = "DEFAULT",
    FLEX = "FLEX",
    PRIORITY = "PRIORITY"
}

export const OPEN_AI_COMPATIBLE_SERVICE_TIERS: OpenAiServiceTier[] = [
    OpenAiServiceTier.DEFAULT,
    OpenAiServiceTier.PRIORITY
];

export enum OpenAiReasoningEffort {
    NONE = "NONE",
    MINIMAL = "MINIMAL",
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
    XHIGH = "XHIGH",
    MAX = "MAX"
}

export enum AnthropicReasoningEffort {
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
    MAX = "MAX"
}

export enum GrokReasoningEffort {
    LOW = "LOW",
    HIGH = "HIGH"
}

export enum AiExtractorWebSearchMode {
    DISABLED = "DISABLED",
    NATIVE = "NATIVE",
    EXTERNAL = "EXTERNAL"
}

export enum ExternalWebSearchBackend {
    BRAVE = "BRAVE",
    OLLAMA = "OLLAMA"
}

export function formatExternalWebSearchBackend(value?: ExternalWebSearchBackend) {
    switch (value) {
        case ExternalWebSearchBackend.BRAVE:
            return "Brave";
        case ExternalWebSearchBackend.OLLAMA:
            return "Ollama cloud";
        default:
            return value;
    }
}

export function formatAiExtractorWebSearchMode(value?: AiExtractorWebSearchMode) {
    switch (value) {
        case AiExtractorWebSearchMode.DISABLED:
            return "disabled";
        case AiExtractorWebSearchMode.NATIVE:
            return "native";
        case AiExtractorWebSearchMode.EXTERNAL:
            return "external";
        default:
            return value;
    }
}

export function formatAiExtractorWebSearchConfig(webSearchMode?: AiExtractorWebSearchMode,
                                                 externalWebSearchBackend?: ExternalWebSearchBackend) {
    if (!webSearchMode || webSearchMode === AiExtractorWebSearchMode.DISABLED) {
        return undefined;
    }

    const formattedMode = formatAiExtractorWebSearchMode(webSearchMode);
    const formattedBackend = webSearchMode === AiExtractorWebSearchMode.EXTERNAL && externalWebSearchBackend
        ? formatExternalWebSearchBackend(externalWebSearchBackend)
        : undefined;

    return [formattedMode, formattedBackend].filter(Boolean).join(" / ") || undefined;
}

export function formatAiExtractorType(type?: AiExtractorType) {
    switch (type) {
        case AiExtractorType.OPENAI_API:
            return "OpenAI API";
        case AiExtractorType.OPENAI_COMPATIBLE_API:
            return "OpenAI-compatible API";
        case AiExtractorType.GEMINI_API:
            return "Gemini API";
        case AiExtractorType.GROK_API:
            return "Grok";
        case AiExtractorType.OLLAMA:
            return "Ollama";
        case AiExtractorType.MISTRAL_AI:
            return "Mistral AI";
        case AiExtractorType.ANTHROPIC_API:
            return "Anthropic";
        default:
            return type;
    }
}

export function isActivelyManagedByInterests(aiExtractor?: InterestManagedAiExtractor) {
    return !!aiExtractor?.managedByInterests && (aiExtractor.aiAttributes?.length ?? 0) > 0;
}

class AiExtractor {
    constructor(
        public id?: string,
        public createdAt?: Date,
        public name?: string,
        public description?: string,
        public type?: AiExtractorType,
        public source?: AiExtractorSource,
        public openAiServiceTier?: OpenAiServiceTier,
        public openAiReasoningEffort?: OpenAiReasoningEffort,
        public anthropicReasoningEffort?: AnthropicReasoningEffort,
        public grokReasoningEffort?: GrokReasoningEffort,
        public webSearchMode?: AiExtractorWebSearchMode,
        public externalWebSearchBackend?: ExternalWebSearchBackend,
        public assigned?: boolean,
        public model?: string,
        public systemPrompt?: AiExtractorPrompt,
        public supplementaryPrompt?: AiExtractorPrompt,
        public aiProviderConnection?: AiExtractorProviderConnection,
        public aiProviderConnectionId?: string,
        public aiAttributes?: AiExtractorAiAttribute[],
        public aiAttributeIds?: string[],
        public vulSources?: AiExtractorVulSource[],
        public updatedItemsBehaviour?: UpdatedItemsBehaviour,
        public itemUpdateProfile?: ItemUpdateProfile,
        public managedByInterests?: boolean,
        public autoRunOnOrganizationSources?: boolean,
        public aiProviderCommunicationLoggingEnabled?: boolean,
        public owner?: ResourceOwnerType,
        public organization?: Organization
    ) {
    }

    public static fromPOJO(data: any) {
        if (!data) return null;

        const entity = new AiExtractor();

        entity.id = data.id;
        entity.createdAt = data.createdAt;
        entity.name = data.name;
        entity.description = data.description;
        entity.type = data.type;
        entity.source = data.source;
        entity.openAiServiceTier = data.openAiServiceTier;
        entity.openAiReasoningEffort = data.openAiReasoningEffort;
        entity.anthropicReasoningEffort = data.anthropicReasoningEffort;
        entity.grokReasoningEffort = data.grokReasoningEffort;
        entity.webSearchMode = data.webSearchMode;
        entity.externalWebSearchBackend = data.externalWebSearchBackend;
        entity.assigned = data.assigned;
        entity.model = data.model;
        entity.systemPrompt = data.systemPrompt;
        entity.supplementaryPrompt = data.supplementaryPrompt;
        entity.aiProviderConnection = data.aiProviderConnection;
        entity.aiProviderConnectionId = data.aiProviderConnection?.id;
        entity.aiAttributes = data.aiAttributes;
        entity.aiAttributeIds = data.aiAttributeIds;
        entity.vulSources = data.vulSources;
        entity.updatedItemsBehaviour = data.updatedItemsBehaviour;
        entity.itemUpdateProfile = ItemUpdateProfile.fromPOJO(data.itemUpdateProfile);
        entity.managedByInterests = data.managedByInterests;
        entity.autoRunOnOrganizationSources = data.autoRunOnOrganizationSources;
        entity.aiProviderCommunicationLoggingEnabled = data.aiProviderCommunicationLoggingEnabled;
        entity.owner = data.owner;
        entity.organization = data.organization ? Organization.fromPOJO(data.organization) : undefined;

        return entity;
    }

    public isReadable(user: User): boolean {
        return user.hasOneOfPermission([Permission.SOURCE__READ_SEAL, Permission.SOURCE__READ_ORG]);
    }
}

export default AiExtractor;
